<template>
    <Card v-if="fetching" customClasses="tw:p-6">
        <Skeleton padding="tw:p-0" variant="tw:bg-transparent">
            <template #skeleton>
                <div class="tw:flex tw:flex-col tw:gap-6">
                    <!-- Nombre -->
                    <div>
                        <SkeletonItem height="tw:h-10" customClass="tw:w-full tw:rounded-lg" />
                    </div>

                    <!-- Tallas (multiselect con tags) -->
                    <div>
                        <SkeletonItem height="tw:h-[52px]" customClass="tw:w-full tw:rounded-lg" />
                    </div>

                    <!-- Stock por talla (tabla con 5 filas) -->
                    <div>
                        <SkeletonItem height="tw:h-4" customClass="tw:w-32 tw:rounded tw:mb-2" />
                        <SkeletonItem height="tw:h-[34px]" customClass="tw:w-full tw:rounded-none" />
                        <div class="tw:divide-y tw:divide-gray-100">
                            <SkeletonItem v-for="n in 5" :key="'stock-row-' + n" height="tw:h-[52px]" customClass="tw:w-full tw:rounded-none" />
                        </div>
                    </div>

                    <!-- Precio -->
                    <div>
                        <SkeletonItem height="tw:h-10" customClass="tw:w-full tw:rounded-lg" />
                        <SkeletonItem height="tw:h-3" customClass="tw:w-64 tw:rounded tw:mt-1" />
                    </div>

                    <!-- Imagen (Dropzone) -->
                    <div>
                        <SkeletonItem height="tw:h-4" customClass="tw:w-44 tw:rounded tw:mb-1" />
                        <SkeletonItem height="tw:h-3" customClass="tw:w-96 tw:rounded tw:mb-2" />
                        <div class="tw:flex tw:flex-col tw:items-center tw:gap-3">
                            <SkeletonItem height="tw:h-48" customClass="tw:w-48 tw:rounded-lg" />
                            <SkeletonItem height="tw:h-8" customClass="tw:w-32 tw:rounded" />
                        </div>
                    </div>

                    <!-- Toggle: Incluida (con texto) -->
                    <div>
                        <div class="tw:flex tw:items-center tw:gap-2">
                            <SkeletonItem height="tw:h-5" customClass="tw:w-9 tw:rounded-full" />
                            <SkeletonItem height="tw:h-4" customClass="tw:flex-1 tw:rounded" />
                        </div>
                        <SkeletonItem height="tw:h-3" customClass="tw:w-3/4 tw:rounded tw:mt-1" />
                    </div>

                    <!-- Cantidad incluida -->
                    <div>
                        <SkeletonItem height="tw:h-10" customClass="tw:w-full tw:rounded-lg" />
                    </div>

                    <!-- Toggle: Obligatoria (con texto) -->
                    <div>
                        <div class="tw:flex tw:items-center tw:gap-2">
                            <SkeletonItem height="tw:h-5" customClass="tw:w-9 tw:rounded-full" />
                            <SkeletonItem height="tw:h-4" customClass="tw:flex-1 tw:rounded" />
                        </div>
                        <SkeletonItem height="tw:h-3" customClass="tw:w-2/3 tw:rounded tw:mt-1" />
                    </div>

                    <!-- Submit -->
                    <div class="tw:flex tw:justify-end tw:pt-4 tw:border-t tw:border-gray-200">
                        <SkeletonItem height="tw:h-9" customClass="tw:w-28 tw:rounded" />
                    </div>
                </div>
            </template>
        </Skeleton>
    </Card>

    <Card v-else customClasses="tw:p-6">
        <ValidationObserver tag="form" ref="observer" @submit.prevent="onSubmit">
            <div class="tw:flex tw:flex-col tw:gap-6">
                <!-- Nombre -->
                <BaseInputText
                    v-model="formData.nombre"
                    label="Nombre de la indumentaria"
                    name="nombre"
                    rules="required"
                />

                <!-- Tallas -->
                <BaseMultiselect
                    :value="tallasSeleccionadasIds"
                    :options="tallasDisponibles"
                    value-key="id"
                    label-key="nombre"
                    label="Seleccionar tallas"
                    :multiple="true"
                    name="talla"
                    rules="required"
                    @input="onTallasChange"
                />

                <!-- Stock por talla -->
                <div v-if="formData.tallasSelect.length" class="tw:mt-4">
                    <h4 class="tw:text-sm tw:font-semibold tw:text-gray-600 tw:mb-2">Stock por talla</h4>
                    <BaseTable
                        :columns="tallasStockColumns"
                        :data="formData.tallasSelect"
                        :skeleton="false"
                        :naked="true"
                        customClass="tw:-mx-6 tw:mb-3"
                        :default-col-width="defaultColWidth"
                    >
                        <template #cell-stock="{ row }">
                            <BaseInputNumber
                                v-model="row.stock_total"
                                :min="0"
                            />
                        </template>
                        <template #cell-stock_seleccionable="{ row }">
                            <BaseInputNumber
                                v-model="row.stock_seleccionable"
                                :min="0"
                            />
                        </template>
                    </BaseTable>
                </div>

                <!-- Precio -->
                <BaseInputNumber
                    v-if="!formData.incluida"
                    v-model="formData.precio"
                    label="Precio"
                    name="precio"
                    rules="required"
                    :step="0.01"
                    :min="0"
                />
                <div v-else>
                    <BaseInputText
                        :value="formatPrice(formData.precio)"
                        label="Precio"
                        disabled
                    />
                    <p class="tw:text-xs tw:text-gray-500 tw:mt-1">
                        Precio del producto incluido: {{ formatPrice(formData.precio) }}
                    </p>
                </div>

                <!-- Imagen -->
                <div>
                    <label class="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1">
                        Imagen de la indumentaria
                    </label>
                    <p class="tw:text-xs tw:text-gray-500 tw:mb-2">
                        MEDIDAS: 450×450 — FORMATO: JPG — PESO MÁXIMO: Menos de 500 KB
                    </p>

                    <Dropzone
                        v-if="uploadUrl"
                        :url="uploadUrl"
                        :width="450"
                        :height="450"
                        :current-image="formData.imagen_formatted || formData.imagen"
                        :preview="localPreview"
                        @success="handleImageUploaded"
                        @removed="handleImageRemoved"
                    />
                </div>

                <!-- Toggle: Incluida -->
                <div>
                    <ToggleSwitch v-model="formData.incluida">
                        Incluir esta indumentaria en el precio de la categoría a la que se asocie
                    </ToggleSwitch>
                    <p class="tw:text-xs tw:text-gray-500 tw:mt-1">
                        *En caso de marcar esta casilla, esta indumentaria irá incluida en el precio de la categoría, por lo que pasará a costar 0 €
                    </p>
                </div>

                <!-- Cantidad incluida -->
                <BaseInputNumber
                    v-if="formData.incluida"
                    v-model="formData.cantidad_incluida"
                    label="Cantidad incluida"
                    name="cantidad_incluida"
                    rules="required"
                    :min="1"
                />

                <!-- Toggle: Obligatoria -->
                <div>
                    <ToggleSwitch v-model="formData.obligatoria">
                        Marcar como obligatoria
                    </ToggleSwitch>
                    <p class="tw:text-xs tw:text-gray-500 tw:mt-1">
                        *Si se marca, el deportista estará obligado a seleccionar una talla de esta indumentaria
                    </p>
                </div>

                <!-- Submit -->
                <div class="tw:flex tw:justify-end tw:pt-4 tw:border-t tw:border-gray-200">
                    <Button type="submit" :disabled="loading" icon="fas fa-save">
                        {{ loading ? 'Guardando...' : 'Guardar' }}
                    </Button>
                </div>
            </div>
        </ValidationObserver>
    </Card>
</template>

<script setup>
    import { ref } from 'vue';
    import { ValidationObserver } from 'vee-validate';
    import Card from '@/modules/admin/ui/Card.vue';
    import { Skeleton, SkeletonItem } from '@/modules/admin/ui/skeleton';
    import BaseInputText from '@/modules/admin/ui/BaseInputText.vue';
    import BaseInputNumber from '@/modules/admin/ui/BaseInputNumber.vue';
    import ToggleSwitch from '@/modules/admin/ui/ToggleSwitch.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import BaseTable from '@/modules/admin/ui/table/BaseTable.vue';
    import Dropzone from '@/modules/admin/ui/Dropzone.vue';
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue';
    import { useIndumentariaForm } from '@/composables/admin/bases/useIndumentariaForm';
    import { useFormatPrice } from '@/composables/common/useFormatPrice';

    defineProps({
        defaultColWidth: {
            type: String,
            default: '120px'
        }
    });

    const formatPrice = useFormatPrice();

    const {
        formData,
        loading,
        fetching,
        uploadUrl,
        localPreview,
        tallasDisponibles,
        tallasSeleccionadasIds,
        guardar,
        handleImageUploaded,
        handleImageRemoved,
        onTallasChange,
    } = useIndumentariaForm();

    const observer = ref(null);

    async function onSubmit() {
        const isValid = await observer.value?.validate();
        if (isValid) {
            guardar();
        }
    }

    const tallasStockColumns = [
        { key: 'nombre', label: 'Talla', centered: true },
        { key: 'stock', label: 'Stock', centered: true },
        { key: 'stock_seleccionable', label: 'Stock máx. seleccionable', centered: true },
    ];
</script>
